import React, { useMemo, useState, useEffect } from 'react';
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import { useDeliveryHistoryDetail } from '../hooks/useDeliveryHistoryDetail';
import DetailFilterSection from './DetailFilterSection';
import SummaryTable from './SummaryTable';
import DetailTable from './DetailTable';
import '@/shared/styles/search-form.css';
import '../styles/DeliveryHistoryDetailPage.css';

const ITEMS_PER_PAGE = 50;

const DeliveryHistoryDetailPage: React.FC = () => {
    const {
        filters,
        results,
        riderSummaries,
        summaryInfo,
        isSummaryView,
        loading,
        error,
        handleFilterChange,
        handleSubmit,
        suppliers,
        suppliersLoading,
        riders,
        ridersLoading,
        isSupplierUser,
        excelDownloading,
        handleExcelDownload,
    } = useDeliveryHistoryDetail();

    const [page, setPage] = useState(1);

    useEffect(() => {
        setPage(1);
    }, [results, riderSummaries]);

    const totalDetailDeliveryAmt = useMemo(
        () => results.reduce((sum, row) => sum + (row.deliveryAmt || 0), 0),
        [results]
    );

    const totalItems = isSummaryView ? riderSummaries.length : results.length;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE) || 0;

    const pagedSummaries = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return riderSummaries.slice(start, start + ITEMS_PER_PAGE);
    }, [page, riderSummaries]);

    const pagedResults = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return results.slice(start, start + ITEMS_PER_PAGE);
    }, [page, results]);

    return (
        <div className="delivery-history-detail-page">
            <div className="page-header">
                <h1 className="page-title">배달 상세 내역</h1>
            </div>

            <DetailFilterSection
                filters={filters}
                isSupplierUser={isSupplierUser}
                suppliers={suppliers}
                suppliersLoading={suppliersLoading}
                riders={riders}
                ridersLoading={ridersLoading}
                loading={loading}
                excelDownloading={excelDownloading}
                onFilterChange={handleFilterChange}
                onSubmit={handleSubmit}
                onExcelDownload={handleExcelDownload}
            />

            {isSummaryView && summaryInfo.totalDeliveryCount !== undefined && (
                <div className="result-count">
                    <i className="fas fa-list" />
                    총 배달 건수: <strong>{summaryInfo.totalDeliveryCount.toLocaleString()}건</strong>
                    <span className="result-count-separator">|</span>
                    총 배달료: <strong>{summaryInfo.totalDeliveryAmt || '0'}원</strong>
                </div>
            )}
            {!isSummaryView && results.length > 0 && (
                <div className="result-count">
                    <i className="fas fa-list" />
                    총 배달 건수: <strong>{results.length.toLocaleString()}건</strong>
                    <span className="result-count-separator">|</span>
                    총 배달료: <strong>{totalDetailDeliveryAmt.toLocaleString()}원</strong>
                </div>
            )}

            <div className="card result-card">
                <div className="card-body">
                    {loading ? (
                        <Loading />
                    ) : error ? (
                        <div className="empty-state">
                            <h3>오류가 발생했습니다</h3>
                            <p>{error}</p>
                        </div>
                    ) : isSummaryView ? (
                        <SummaryTable riderSummaries={pagedSummaries} />
                    ) : (
                        <DetailTable results={pagedResults} />
                    )}
                    {totalPages > 1 && (
                        <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeliveryHistoryDetailPage;
